/**
 * Fase 7 — regras automáticas: enums, schemas Zod, tipos da API e as funções puras de
 * avaliação (condições E/OU com janelas, limites de segurança, cálculo de novo orçamento).
 *
 * Semântica das condições (documentada também no README): a regra tem uma lista de GRUPOS;
 * dentro de um grupo as condições são ligadas por **E**; entre grupos, por **OU**.
 * A regra "bate" para um item quando pelo menos um grupo tem todas as condições verdadeiras.
 *
 * Unidades dos valores das condições (como a pessoa vê na tela):
 * - gasto, custo por resultado, CPC, CPM, CPL: na moeda da conta (ex.: 12.50), não em centavos;
 * - CTR: em % (0–100); ROAS e frequência: razão; resultados, impressões e leads: contagem.
 */
import { z } from "zod";
import {
  applyBudgetChange,
  CAMPAIGN_LEVELS,
  validateBudgetCents,
  type BudgetKind,
  type CampaignLevel,
} from "./campaign-enums.js";
import type { Metrics } from "./reports-enums.js";

export const RULE_METRICS = [
  "gasto",
  "resultados",
  "custo_por_resultado",
  "ctr",
  "cpc",
  "cpm",
  "roas",
  "frequencia",
  "impressoes",
  "leads",
  "cpl",
] as const;
export type RuleMetric = (typeof RULE_METRICS)[number];

export const RULE_METRIC_LABELS: Record<RuleMetric, string> = {
  gasto: "Gasto",
  resultados: "Resultados",
  custo_por_resultado: "Custo por resultado",
  ctr: "CTR (%)",
  cpc: "CPC",
  cpm: "CPM",
  roas: "ROAS",
  frequencia: "Frequência",
  impressoes: "Impressões",
  leads: "Leads",
  cpl: "Custo por lead",
};

/** Como o valor da condição é mostrado/digitado. */
export const RULE_METRIC_FORMAT: Record<RuleMetric, "money" | "int" | "pct" | "ratio"> = {
  gasto: "money",
  resultados: "int",
  custo_por_resultado: "money",
  ctr: "pct",
  cpc: "money",
  cpm: "money",
  roas: "ratio",
  frequencia: "ratio",
  impressoes: "int",
  leads: "int",
  cpl: "money",
};

export const RULE_OPERATORS = [">", ">=", "<", "<=", "entre"] as const;
export type RuleOperator = (typeof RULE_OPERATORS)[number];

export const RULE_OPERATOR_LABELS: Record<RuleOperator, string> = {
  ">": "maior que",
  ">=": "maior ou igual a",
  "<": "menor que",
  "<=": "menor ou igual a",
  entre: "entre",
};

export const RULE_WINDOWS = ["hoje", "ontem", "3d", "7d", "14d", "30d", "vitalicio"] as const;
export type RuleWindow = (typeof RULE_WINDOWS)[number];

export const RULE_WINDOW_LABELS: Record<RuleWindow, string> = {
  hoje: "hoje",
  ontem: "ontem",
  "3d": "últimos 3 dias",
  "7d": "últimos 7 dias",
  "14d": "últimos 14 dias",
  "30d": "últimos 30 dias",
  vitalicio: "vitalício",
};

export const RULE_ACTION_TYPES = [
  "pausar",
  "ativar",
  "aumentar_orcamento",
  "diminuir_orcamento",
  "definir_orcamento",
  "notificar",
] as const;
export type RuleActionType = (typeof RULE_ACTION_TYPES)[number];

export const RULE_ACTION_LABELS: Record<RuleActionType, string> = {
  pausar: "Pausar",
  ativar: "Ativar",
  aumentar_orcamento: "Aumentar orçamento",
  diminuir_orcamento: "Diminuir orçamento",
  definir_orcamento: "Definir orçamento",
  notificar: "Notificar",
};

export const RULE_FREQUENCIES = [15, 30, 60] as const;
export type RuleFrequency = (typeof RULE_FREQUENCIES)[number];

export const RULE_RUN_MODES = ["simular", "executar"] as const;
export type RuleRunMode = (typeof RULE_RUN_MODES)[number];

/** Status de entrega em que a regra NUNCA mexe (erro, revisão, processamento, excluído). */
export const RULE_PROTECTED_STATUSES: readonly string[] = [
  "DISAPPROVED",
  "PENDING_REVIEW",
  "WITH_ISSUES",
  "IN_PROCESS",
  "PENDING_BILLING_INFO",
  "DELETED",
  "ARCHIVED",
];

export function isProtectedStatus(effectiveStatus: string | null | undefined): boolean {
  return !!effectiveStatus && RULE_PROTECTED_STATUSES.includes(effectiveStatus);
}

// ---- Schemas ------------------------------------------------------------------------------------

const metaId = z.string().trim().regex(/^(act_)?\d{1,30}$/, "Informe um id numérico da Meta.");

export const ruleConditionSchema = z
  .object({
    metric: z.enum(RULE_METRICS, { message: "Escolha a métrica." }),
    operator: z.enum(RULE_OPERATORS, { message: "Escolha o operador." }),
    value: z.number({ message: "Informe um número." }),
    /** Só para `entre` (limite superior). */
    value2: z.number().optional(),
    window: z.enum(RULE_WINDOWS, { message: "Escolha a janela." }),
  })
  .refine((c) => c.operator !== "entre" || (c.value2 !== undefined && c.value2 >= c.value), {
    message: "Em 'entre', informe o segundo valor maior ou igual ao primeiro.",
    path: ["value2"],
  });
export type RuleCondition = z.infer<typeof ruleConditionSchema>;

export const ruleConditionGroupSchema = z.object({
  conditions: z.array(ruleConditionSchema).min(1, "Adicione pelo menos uma condição."),
});
export type RuleConditionGroup = z.infer<typeof ruleConditionGroupSchema>;

export const ruleSelectionSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("ids"), ids: z.array(metaId).min(1, "Escolha pelo menos um item.") }),
  z.object({
    mode: z.literal("nome"),
    contains: z.string().trim().max(120).optional(),
    notContains: z.string().trim().max(120).optional(),
  }),
]);
export type RuleSelection = z.infer<typeof ruleSelectionSchema>;

export const ruleScopeSchema = z.object({
  level: z.enum(CAMPAIGN_LEVELS, { message: "Escolha o nível." }),
  adAccountMetaIds: z.array(metaId).min(1, "Escolha pelo menos uma conta de anúncio."),
  selection: ruleSelectionSchema,
});
export type RuleScope = z.infer<typeof ruleScopeSchema>;

export const ruleActionSchema = z
  .object({
    type: z.enum(RULE_ACTION_TYPES, { message: "Escolha a ação." }),
    /** Variação em % (aumentar/diminuir) ou valor na moeda da conta (definir / aumentar por valor). */
    mode: z.enum(["percentual", "valor"]).optional(),
    value: z.number().positive("Informe um valor maior que zero.").optional(),
  })
  .refine(
    (a) =>
      !["aumentar_orcamento", "diminuir_orcamento", "definir_orcamento"].includes(a.type) ||
      (a.value !== undefined && a.value > 0),
    { message: "Informe o valor da mudança de orçamento.", path: ["value"] },
  )
  .refine((a) => a.type !== "diminuir_orcamento" || a.mode !== "percentual" || (a.value ?? 0) < 100, {
    message: "Diminuir em % precisa ser menor que 100%.",
    path: ["value"],
  });
export type RuleAction = z.infer<typeof ruleActionSchema>;

export const ruleSafetySchema = z.object({
  /** Na moeda da conta (não em centavos). Null = sem limite. */
  maxBudget: z.number().positive().nullable().default(null),
  minBudget: z.number().positive().nullable().default(null),
  maxActionsPerItemPerDay: z.number().int().min(1).max(50).default(1),
  cooldownMinutes: z.number().int().min(0).max(24 * 60).default(60),
});
export type RuleSafety = z.infer<typeof ruleSafetySchema>;

export const ruleBodySchema = z
  .object({
    name: z.string().trim().min(2, "Dê um nome à regra.").max(120),
    clientId: z.uuid().nullable().default(null),
    active: z.boolean().default(true),
    scope: ruleScopeSchema,
    groups: z.array(ruleConditionGroupSchema).min(1, "Adicione pelo menos um grupo de condições."),
    actions: z.array(ruleActionSchema).min(1, "Escolha pelo menos uma ação."),
    safety: ruleSafetySchema.default({
      maxBudget: null,
      minBudget: null,
      maxActionsPerItemPerDay: 1,
      cooldownMinutes: 60,
    }),
    frequencyMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]).default(15),
  })
  .superRefine((r, ctx) => {
    const status = r.actions.filter((a) => a.type === "pausar" || a.type === "ativar");
    if (status.length > 1)
      ctx.addIssue({ code: "custom", message: "Escolha só pausar OU ativar.", path: ["actions"] });
    const budget = r.actions.filter((a) => a.type.endsWith("_orcamento"));
    if (budget.length > 1)
      ctx.addIssue({ code: "custom", message: "Escolha só uma ação de orçamento.", path: ["actions"] });
    if (budget.length > 0 && r.scope.level === "ads")
      ctx.addIssue({
        code: "custom",
        message: "Anúncio não tem orçamento: ações de orçamento valem para campanhas e conjuntos.",
        path: ["actions"],
      });
    if (
      r.safety.maxBudget !== null &&
      r.safety.minBudget !== null &&
      r.safety.maxBudget < r.safety.minBudget
    )
      ctx.addIssue({
        code: "custom",
        message: "O orçamento máximo precisa ser maior que o mínimo.",
        path: ["safety", "maxBudget"],
      });
  });
export type RuleBodyInput = z.input<typeof ruleBodySchema>;
export type RuleBody = z.output<typeof ruleBodySchema>;

export const createRuleSchema = ruleBodySchema;
export type CreateRuleInput = z.input<typeof createRuleSchema>;
export const updateRuleSchema = ruleBodySchema;
export type UpdateRuleInput = z.input<typeof updateRuleSchema>;

export const ruleRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

// ---- DTOs ---------------------------------------------------------------------------------------

export interface RuleDto extends RuleBody {
  id: string;
  createdByName: string | null;
  lastRunAt: string | null;
  lastRunMode: RuleRunMode | null;
  lastRunStatus: "ok" | "erro" | null;
  lastRunMatched: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RuleConditionResult {
  metric: RuleMetric;
  operator: RuleOperator;
  value: number;
  value2: number | null;
  window: RuleWindow;
  /** Valor observado na janela (unidade da tela), null quando não há dado. */
  actual: number | null;
  passed: boolean;
}

export interface RuleRunItemDto {
  level: CampaignLevel;
  metaId: string;
  name: string;
  adAccountMetaId: string;
  effectiveStatus: string;
  matched: boolean;
  /** Resultado por grupo (E dentro, OU entre). */
  groups: { passed: boolean; conditions: RuleConditionResult[] }[];
  /** O que a regra faria/fez com este item. */
  actions: RuleRunActionDto[];
}

export interface RuleRunActionDto {
  type: RuleActionType;
  /** `planejada` (simulação), `executada`, `pulada` (limite de segurança) ou `falhou`. */
  status: "planejada" | "executada" | "pulada" | "falhou";
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface RuleRunDto {
  id: string;
  ruleId: string;
  ruleName: string;
  mode: RuleRunMode;
  trigger: "manual" | "cron";
  adAccountMetaId: string;
  status: "ok" | "erro";
  error: string | null;
  evaluated: number;
  matched: number;
  executed: number;
  skipped: number;
  failed: number;
  items: RuleRunItemDto[];
  startedAt: string;
  finishedAt: string | null;
  startedByName: string | null;
}

export interface RulePreviewDto {
  evaluated: number;
  matched: number;
  items: RuleRunItemDto[];
  /** Avisos (ex.: métricas desatualizadas, conta sem dados). */
  warnings: string[];
}

export interface RuleTemplateDto {
  id: string;
  name: string;
  description: string;
  body: Omit<RuleBodyInput, "scope"> & { scope: Omit<RuleScope, "adAccountMetaIds"> };
}

// ---- Avaliação (puro) ---------------------------------------------------------------------------

/** Valor de uma métrica de regra na unidade da tela, a partir das somas/derivadas do período. */
export function ruleMetricValue(m: Metrics | null | undefined, metric: RuleMetric): number | null {
  if (!m) return null;
  switch (metric) {
    case "gasto":
      return m.spendCents / 100;
    case "resultados":
      return m.results;
    case "custo_por_resultado":
      return m.costPerResultCents === null ? null : m.costPerResultCents / 100;
    case "ctr":
      return m.ctr;
    case "cpc":
      return m.cpcCents === null ? null : m.cpcCents / 100;
    case "cpm":
      return m.cpmCents === null ? null : m.cpmCents / 100;
    case "roas":
      return m.roas;
    case "frequencia":
      return m.frequency;
    case "impressoes":
      return m.impressions;
    case "leads":
      return m.leads;
    case "cpl":
      return m.costPerLeadCents === null ? null : m.costPerLeadCents / 100;
  }
}

export function compareRuleValue(
  operator: RuleOperator,
  actual: number | null,
  value: number,
  value2?: number | null,
): boolean {
  if (actual === null) return false;
  switch (operator) {
    case ">":
      return actual > value;
    case ">=":
      return actual >= value;
    case "<":
      return actual < value;
    case "<=":
      return actual <= value;
    case "entre":
      return actual >= value && actual <= (value2 ?? value);
  }
}

/** Métricas por janela de um item (null = sem dado na janela). */
export type MetricsByWindow = Partial<Record<RuleWindow, Metrics | null>>;

/** Avalia os grupos (E dentro, OU entre) para um item. Função pura. */
export function evaluateRuleGroups(
  groups: RuleConditionGroup[],
  metrics: MetricsByWindow,
): { matched: boolean; groups: { passed: boolean; conditions: RuleConditionResult[] }[] } {
  const results = groups.map((g) => {
    const conditions = g.conditions.map((c): RuleConditionResult => {
      const actual = ruleMetricValue(metrics[c.window], c.metric);
      const rounded = actual === null ? null : Math.round(actual * 10000) / 10000;
      return {
        metric: c.metric,
        operator: c.operator,
        value: c.value,
        value2: c.value2 ?? null,
        window: c.window,
        actual: rounded,
        passed: compareRuleValue(c.operator, actual, c.value, c.value2),
      };
    });
    return { passed: conditions.every((c) => c.passed), conditions };
  });
  return { matched: results.some((g) => g.passed), groups: results };
}

/** Janelas usadas pelas condições (para buscar métricas só do necessário). */
export function ruleWindows(groups: RuleConditionGroup[]): RuleWindow[] {
  const out = new Set<RuleWindow>();
  for (const g of groups) for (const c of g.conditions) out.add(c.window);
  return [...out];
}

/** Intervalo de datas (inclusivo) de uma janela, dado o "hoje" no fuso da conta. */
export function ruleWindowRange(
  window: RuleWindow,
  today: string,
  addDaysFn: (iso: string, days: number) => string,
): { from: string | null; to: string } {
  switch (window) {
    case "hoje":
      return { from: today, to: today };
    case "ontem": {
      const y = addDaysFn(today, -1);
      return { from: y, to: y };
    }
    case "3d":
      return { from: addDaysFn(today, -2), to: today };
    case "7d":
      return { from: addDaysFn(today, -6), to: today };
    case "14d":
      return { from: addDaysFn(today, -13), to: today };
    case "30d":
      return { from: addDaysFn(today, -29), to: today };
    case "vitalicio":
      return { from: null, to: today };
  }
}

export interface BudgetPlan {
  nextCents: number;
  /** Verdadeiro quando o limite de segurança segurou o valor. */
  clamped: boolean;
  /** Motivo para NÃO executar (já no limite, abaixo do mínimo da Meta, etc.). */
  skipReason: string | null;
}

/**
 * Novo orçamento (centavos) de uma ação de orçamento, já com os limites de segurança
 * (máximo/mínimo absolutos em centavos) e a validação de mínimo da Meta por moeda.
 */
export function planBudgetChange(
  currentCents: number,
  action: RuleAction,
  safety: { maxBudgetCents: number | null; minBudgetCents: number | null },
  currency: string,
  kind: BudgetKind,
): BudgetPlan {
  const value = action.value ?? 0;
  let next: number;
  if (action.type === "definir_orcamento") next = Math.round(value * 100);
  else if (action.mode === "percentual") {
    const pct = action.type === "aumentar_orcamento" ? value : -value;
    next = applyBudgetChange(currentCents, { mode: "percentual", value: pct });
  } else {
    const delta = Math.round(value * 100);
    next = currentCents + (action.type === "aumentar_orcamento" ? delta : -delta);
  }
  let clamped = false;
  if (safety.maxBudgetCents !== null && next > safety.maxBudgetCents) {
    next = safety.maxBudgetCents;
    clamped = true;
  }
  if (safety.minBudgetCents !== null && next < safety.minBudgetCents) {
    next = safety.minBudgetCents;
    clamped = true;
  }
  if (next === currentCents) {
    return {
      nextCents: next,
      clamped,
      skipReason: clamped
        ? "O orçamento já está no limite de segurança da regra."
        : "O orçamento não mudaria.",
    };
  }
  const problem = validateBudgetCents(next, currency, kind);
  if (problem) return { nextCents: next, clamped, skipReason: problem };
  return { nextCents: next, clamped, skipReason: null };
}

/** Chave da janela de execução (idempotência do Cron): bucket de `frequencyMinutes`. */
export function ruleWindowKey(frequencyMinutes: number, now: Date): string {
  const bucket = Math.floor(now.getTime() / (frequencyMinutes * 60_000));
  return `${frequencyMinutes}:${bucket}`;
}

/** A regra está "vencida" para rodar agora? (última execução antes do bucket atual) */
export function ruleIsDue(
  rule: { frequencyMinutes: number; lastRunAt: string | Date | null },
  now: Date,
): boolean {
  if (!rule.lastRunAt) return true;
  const last = new Date(rule.lastRunAt).getTime();
  return now.getTime() - last >= rule.frequencyMinutes * 60_000 - 30_000;
}

// ---- Templates ----------------------------------------------------------------------------------

export const RULE_TEMPLATES: RuleTemplateDto[] = [
  {
    id: "pausar_cpa_alto",
    name: "Pausar anúncio com CPA 50% acima da meta",
    description:
      "Depois de R$ 100 gastos nos últimos 7 dias, pausa o anúncio cujo custo por resultado passou de 1,5× a meta (ajuste o valor pela meta do cliente).",
    body: {
      name: "Pausar anúncio com CPA alto",
      scope: { level: "ads", selection: { mode: "nome" } },
      groups: [
        {
          conditions: [
            { metric: "gasto", operator: ">=", value: 100, window: "7d" },
            { metric: "custo_por_resultado", operator: ">", value: 75, window: "7d" },
          ],
        },
      ],
      actions: [{ type: "pausar" }, { type: "notificar" }],
      safety: { maxBudget: null, minBudget: null, maxActionsPerItemPerDay: 1, cooldownMinutes: 60 },
      frequencyMinutes: 30,
    },
  },
  {
    id: "escalar_roas",
    name: "Escalar 20% conjunto com ROAS > 3 por 3 dias",
    description:
      "Aumenta em 20% o orçamento do conjunto cujo ROAS dos últimos 3 dias está acima de 3, no máximo uma vez por dia e até o teto de segurança.",
    body: {
      name: "Escalar conjuntos com ROAS alto",
      scope: { level: "adsets", selection: { mode: "nome" } },
      groups: [
        {
          conditions: [
            { metric: "roas", operator: ">", value: 3, window: "3d" },
            { metric: "gasto", operator: ">=", value: 50, window: "3d" },
          ],
        },
      ],
      actions: [{ type: "aumentar_orcamento", mode: "percentual", value: 20 }],
      safety: { maxBudget: 500, minBudget: null, maxActionsPerItemPerDay: 1, cooldownMinutes: 1440 },
      frequencyMinutes: 60,
    },
  },
  {
    id: "alertar_frequencia",
    name: "Alertar frequência > 3",
    description:
      "Avisa (app, e-mail e webhook) quando a frequência dos últimos 7 dias de um conjunto passa de 3, sem mexer em nada.",
    body: {
      name: "Alertar frequência alta",
      scope: { level: "adsets", selection: { mode: "nome" } },
      groups: [{ conditions: [{ metric: "frequencia", operator: ">", value: 3, window: "7d" }] }],
      actions: [{ type: "notificar" }],
      safety: { maxBudget: null, minBudget: null, maxActionsPerItemPerDay: 1, cooldownMinutes: 1440 },
      frequencyMinutes: 60,
    },
  },
  {
    id: "pausar_ctr_baixo",
    name: "Pausar anúncio com CTR < 0,5% após 2.000 impressões",
    description:
      "Pausa anúncios que já tiveram 2.000 impressões nos últimos 7 dias e CTR abaixo de 0,5%.",
    body: {
      name: "Pausar anúncios com CTR baixo",
      scope: { level: "ads", selection: { mode: "nome" } },
      groups: [
        {
          conditions: [
            { metric: "impressoes", operator: ">=", value: 2000, window: "7d" },
            { metric: "ctr", operator: "<", value: 0.5, window: "7d" },
          ],
        },
      ],
      actions: [{ type: "pausar" }],
      safety: { maxBudget: null, minBudget: null, maxActionsPerItemPerDay: 1, cooldownMinutes: 60 },
      frequencyMinutes: 30,
    },
  },
  {
    id: "reduzir_cpl_alto",
    name: "Reduzir 30% o orçamento do conjunto com CPL alto",
    description:
      "Diminui em 30% o orçamento diário do conjunto cujo custo por lead de ontem passou de R$ 40, respeitando o piso de segurança.",
    body: {
      name: "Reduzir conjuntos com CPL alto",
      scope: { level: "adsets", selection: { mode: "nome" } },
      groups: [
        {
          conditions: [
            { metric: "cpl", operator: ">", value: 40, window: "ontem" },
            { metric: "leads", operator: ">=", value: 1, window: "ontem" },
          ],
        },
      ],
      actions: [{ type: "diminuir_orcamento", mode: "percentual", value: 30 }, { type: "notificar" }],
      safety: { maxBudget: null, minBudget: 20, maxActionsPerItemPerDay: 1, cooldownMinutes: 1440 },
      frequencyMinutes: 60,
    },
  },
];

export const RULE_LEVEL_LABELS: Record<CampaignLevel, string> = {
  campaigns: "Campanhas",
  adsets: "Conjuntos",
  ads: "Anúncios",
};

/** Resumo em uma linha da regra ("SE … ENTÃO …"), para listas e e-mails. */
export function describeRule(rule: Pick<RuleBody, "groups" | "actions" | "scope">): string {
  const groups = rule.groups
    .map((g) =>
      g.conditions
        .map(
          (c) =>
            `${RULE_METRIC_LABELS[c.metric]} ${RULE_OPERATOR_LABELS[c.operator]} ${c.value}${
              c.operator === "entre" ? ` e ${c.value2 ?? c.value}` : ""
            } (${RULE_WINDOW_LABELS[c.window]})`,
        )
        .join(" E "),
    )
    .join(" OU ");
  const actions = rule.actions
    .map((a) => {
      const label = RULE_ACTION_LABELS[a.type];
      if (a.type.endsWith("_orcamento") && a.value !== undefined) {
        return `${label} ${a.mode === "percentual" ? `${a.value}%` : `em ${a.value}`}`;
      }
      return label;
    })
    .join(" + ");
  return `SE ${groups} ENTÃO ${actions}`;
}
